import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Sparkles, TrendingUp, TrendingDown, Building2, Trophy, Filter, ArrowUpDown } from 'lucide-react';
import { FilterOptions } from '../types';

interface NavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  filters?: FilterOptions;
  onFiltersChange?: (filters: FilterOptions) => void;
  isOpen?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({
  activeTab,
  onTabChange,
  filters = {},
  onFiltersChange,
  isOpen = true
}) => {
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, to: '/overview', gradient: 'from-blue-500 to-indigo-600' },
    { id: 'all', label: 'All Recos', icon: Sparkles, to: '/dashboard', gradient: 'from-purple-500 to-pink-600' },
    { id: 'new', label: 'New Recos', icon: Sparkles, to: '/dashboard', gradient: 'from-cyan-500 to-blue-600' },
    { id: 'high-upside', label: 'High Upside', icon: TrendingUp, to: '/dashboard', gradient: 'from-green-500 to-emerald-600' },
    { id: 'high-downside', label: 'High Downside', icon: TrendingDown, to: '/dashboard', gradient: 'from-red-500 to-rose-600' },
    { id: 'top-stocks', label: 'Top Stocks', icon: Trophy, to: '/top-stocks', gradient: 'from-amber-500 to-orange-600' },
    { id: 'brokerages', label: 'By Brokerages', icon: Building2, to: '/brokerages', gradient: 'from-indigo-500 to-purple-600' },
  ];

  const recommendations = [
    { id: 'buy', label: 'Buy', color: 'text-green-600' },
    { id: 'sell', label: 'Sell', color: 'text-red-600' },
    { id: 'hold', label: 'Hold', color: 'text-yellow-600' },
    { id: 'strong-buy', label: 'Strong Buy', color: 'text-green-700' },
    { id: 'strong-sell', label: 'Strong Sell', color: 'text-red-700' },
  ];

  const sortOptions = [
    { id: 'potential_upside', label: 'Potential Upside' },
    { id: 'call_date', label: 'Date' },
    { id: 'target_price', label: 'Target Price' },
    { id: 'current_price', label: 'Current Price' },
  ];

  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    if (onFiltersChange) {
      onFiltersChange({
        ...filters,
        [key]: value === filters[key] ? undefined : value
      });
    }
  };

  return (
    <div className={`bg-obsidian text-white border-r border-gray-200 ${isOpen ? 'block' : 'hidden'} md:block min-h-full shadow-sm`}>
      {/* Main Navigation Tabs */}
      <div className="p-5">
        <div className="mb-6">
          <h2 className="text-xs text-white font-bold uppercase tracking-wider mb-4">Categories</h2>
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <NavLink
                key={tab.id}
                to={tab.to}
                onClick={() => onTabChange(tab.id)}
                className={({ isActive }) =>
                  `w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center text-white gap-3 group relative overflow-hidden ${
                    isActive && activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg`
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                {({ isActive }) => {
                  const Icon = tab.icon;
                  return (
                    <>
                      {isActive && activeTab === tab.id && (
                        <div className="absolute inset-0 bg-white/20" />
                      )}
                      <div className="relative z-10 flex items-center gap-3 w-full">
                        <Icon className={`h-5 w-5 ${isActive && activeTab === tab.id ? 'text-white' : 'text-white/50 group-hover:text-white'} transition-colors flex-shrink-0`} />
                        <span className="font-medium truncate">{tab.label}</span>
                      </div>
                    </>
                  );
                }}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 pb-5 border-t border-gray-100 pt-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
            <Filter className="h-4 w-4 text-white" />
          </div>
          <h3 className="text-xs font-bold text-white uppercase tracking-wider">Filters</h3>
        </div>

        {/* Recommendation Type Filter */}
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-white mb-3">Recommendation</h4>
          <div className="space-y-1.5">
            {recommendations.map((reco) => (
              <button
                key={reco.id}
                onClick={() => handleFilterChange('recommendation', reco.id as any)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  filters.recommendation === reco.id
                    ? 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span className={reco.color}>{reco.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Upside Range Filter */}
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-gray-600 mb-3">Potential Upside</h4>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              placeholder="Min %"
              value={filters.minUpside || ''}
              onChange={(e) => handleFilterChange('minUpside', e.target.value ? Number(e.target.value) : undefined)}
              className="px-3 py-2 text-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <input
              type="number"
              placeholder="Max %"
              value={filters.maxUpside || ''}
              onChange={(e) => handleFilterChange('maxUpside', e.target.value ? Number(e.target.value) : undefined)}
              className="px-3 py-2 text-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-white mb-3">Date Range</h4>
          <div className="space-y-2">
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value || undefined)}
              className="w-full px-3 py-2 text-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => handleFilterChange('dateTo', e.target.value || undefined)}
              className="w-full px-3 py-2 text-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Clear Filters */}
        {Object.keys(filters).some(key => filters[key as keyof FilterOptions]) && (
          <button
            onClick={() => onFiltersChange && onFiltersChange({})}
            className="w-full text-sm font-medium text-gray-700 hover:text-gray-900 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Sort Options */}
      <div className="px-5 pb-5 border-t border-white/30 pt-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg">
            <ArrowUpDown className="h-4 w-4 text-white" />
          </div>
          <h3 className="text-xs font-bold text-white uppercase tracking-wider">Sort By</h3>
        </div>

        <div className="space-y-1.5 mb-4">
          {sortOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleFilterChange('sortBy', option.id as any)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                filters.sortBy === option.id
                  ? 'bg-gradient-to-r from-gray-100 to-gray-50 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Sort Order */}
        {filters.sortBy && (
          <div className="flex rounded-lg border border-white/30 overflow-hidden">
            <button
              onClick={() => handleFilterChange('sortOrder', 'desc')}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-all ${
                filters.sortOrder === 'desc' || !filters.sortOrder
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              ↓ Desc
            </button>
            <button
              onClick={() => handleFilterChange('sortOrder', 'asc')}
              className={`flex-1 px-3 py-2 text-sm font-medium border-l border-white/30 transition-all ${
                filters.sortOrder === 'asc'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              ↑ Asc
            </button>
          </div>
        )}
      </div>
    </div>
  );
};